import { useCallback } from 'react';
import { axiosClient } from 'utils/gql/axios/AxiosClient';
import JsFileDownload from 'js-file-download';
import { useMixpanel } from 'integrations/mixpanel/useMixpanel';
import { useSnackbar } from 'src/utils/hooks/useSnackbar';
import { MixpanelEvent } from 'src/integrations/mixpanel/MixPanelTypes';
import { OcCsvExportQueryModelInput, OcExportTypeModel, OcReportSourceTypeModel } from 'src/graphql/types';
import { useTypedMutation } from '../../useTypedMutation';
import { ExportDocument } from './utils/__generated__/Export.graphql';
import { ExportEntityUnionType } from './utils/Exports.types';
import { toOcExportEntityModel } from './utils/toOcExportEntityModel';

interface Props {
  mixpanelEvent?: MixpanelEvent;
}
const EMPTY_PROPS = {};
export const useExportsTo = ({ mixpanelEvent }: Props = EMPTY_PROPS) => {
  const { showSuccess, showError } = useSnackbar();
  const { set: exportData } = useTypedMutation(ExportDocument, {
    onCompleted: () => {
      if (mixpanelEvent) {
        track(mixpanelEvent, { exportResult: 'success' });
      }
      showSuccess('export.queued');
    },
    onError: () => {
      showError('export.failed');
      if (mixpanelEvent) {
        track(mixpanelEvent, { exportResult: 'fail' });
      }
    }
  });

  const { track } = useMixpanel();

  return useCallback(
    async ({ mode, query, reportName, source }: ExportEntityUnionType) => {
      if (mode === 'syncCsv') {
        const { headers, data } = await axiosClient.post<string>(getExportUrl(source), query ?? {});

        const name = headers?.['content-disposition']?.split(';')[1].split('=')[1].trim();
        JsFileDownload(data, name);
        showSuccess('export.completed');
        if (mixpanelEvent) {
          track(mixpanelEvent, { exportResult: 'success' });
        }
        return;
      }

      await exportData({
        variables: {
          request: {
            format: mode === 'batch-csv' ? OcExportTypeModel.Csv : OcExportTypeModel.Excel,
            sourceType: OcReportSourceTypeModel.Hub,
            exportEntity: toOcExportEntityModel[source as keyof typeof toOcExportEntityModel],
            reportName,
            csvExportQueryModel: query as OcCsvExportQueryModelInput
          }
        }
      });
    },
    [exportData, mixpanelEvent, showSuccess, track]
  );
};

export const getExportUrl = (source: ExportEntityUnionType['source']): string => {
  const sourceLowerCase = source.toLocaleLowerCase();
  return `/er/api/csv/${sourceLowerCase}`;
};
